import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env";
import { BackofficeBoschDTO } from "@shared/models/backoffice-bosch-dto";
import { ClientDTO } from "@shared/models/client-dto";
import { PageManager } from "@shared/models/page-manager";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
  ClientFranchiseFilterDTO,
  FranchiseDTO,
  FranchiseResponseDTO,
  FranchiseSimplifiedDTO
} from "src/app/modules/private/modules/franchises/models/franchises-response-dto";

@Injectable({
  providedIn: "root"
})
export class FranchiseService {
  private franchisesUrl = "franchise/";

  constructor(private http: HttpClient) {}

  saveFranchises(
    dto: FranchiseDTO
  ): Observable<FranchiseResponseDTO<FranchiseDTO>> {
    return this.http.post<FranchiseResponseDTO<FranchiseDTO>>(
      environment.apiUrl + this.franchisesUrl,
      dto
    );
  }

  getAllFranchises(
    pageManager: PageManager,
    sort = "",
    filters?: {
      name?: string;
      description?: string;
      backofficeTypes?: string;
    }
  ): Observable<FranchiseResponseDTO<FranchiseDTO>> {
    let params = new HttpParams()
      .set("size", pageManager.pageSize.toString())
      .set("page", pageManager.pageIndex.toString());

    if (sort) {
      params = params.set("sort", sort.replace("&sort=", ""));
    }
    if (filters?.name) {
      params = params.set("name", filters.name);
    }
    if (filters?.description) {
      params = params.set("description", filters.description);
    }
    if (filters?.backofficeTypes) {
      params = params.append("backofficeTypes", filters.backofficeTypes);
    }
    return this.http.get<FranchiseResponseDTO<FranchiseDTO>>(
      `${environment.apiUrl}${this.franchisesUrl}`,
      { params }
    );
  }

  getFranchiseById(id: number): Observable<FranchiseResponseDTO<FranchiseDTO>> {
    return this.http
      .get<FranchiseDTO>(`${environment.apiUrl}${this.franchisesUrl}${id}`)
      .pipe(
        map((franchiseData) => {
          return {
            message: "Success",
            payLoad: franchiseData
          };
        })
      );
  }

  editFranchises(
    id: number,
    dto: FranchiseDTO
  ): Observable<FranchiseResponseDTO<FranchiseDTO>> {
    return this.http.put<FranchiseResponseDTO<FranchiseDTO>>(
      `${environment.apiUrl}${this.franchisesUrl}${id}`,
      dto
    );
  }

  getAllClientsByFranchises(
    filter: ClientFranchiseFilterDTO,
    pageManager: PageManager,
    sort = ""
  ): Observable<ClientDTO> {
    let params = new HttpParams()
      .set("page", pageManager.pageIndex.toString())
      .set("size", pageManager.pageSize.toString());

    if (sort) {
      params = params.set("sort", sort.replace("&sort=", ""));
    }
    return this.http.post<ClientDTO>(
      `${environment.apiUrl}clients/franchises`,
      filter,
      { params }
    );
  }

  addClientToFranchises(
    franchiseId: number,
    clientId: number
  ): Observable<void> {
    const params = new HttpParams()
      .set("franchiseId", franchiseId.toString())
      .set("clientId", clientId.toString());

    return this.http.patch<void>(
      `${environment.apiUrl}clients/add-franchise`,
      {},
      { params }
    );
  }

  deleteClientByFranchise(
    franchiseId: number,
    clientId: number
  ): Observable<void> {
    const params = new HttpParams()
      .set("franchiseId", franchiseId.toString())
      .set("clientId", clientId.toString());

    return this.http.patch<void>(
      `${environment.apiUrl}clients/remove-franchise`,
      {},
      { params }
    );
  }

  deleteBackofficeByFranchise(
    franchiseId: number,
    backofficeUserId: number
  ): Observable<void> {
    const params = new HttpParams()
      .set("franchiseId", franchiseId.toString())
      .set("backofficeUserId", backofficeUserId.toString());

    return this.http.patch<void>(
      `${environment.apiUrl}${this.franchisesUrl}remove-backoffice-user`,
      {},
      { params }
    );
  }

  getFranchisesByLoggedUser(): Observable<FranchiseSimplifiedDTO[]> {
    return this.http.get<FranchiseSimplifiedDTO[]>(
      `${environment.apiUrl}${this.franchisesUrl}logged-user`
    );
  }

  isDriveBFranchiseUser(): Observable<boolean> {
    return this.getFranchisesByLoggedUser().pipe(
      map((franchises) => franchises.some((f) => f.name?.includes("DriveB"))),
      catchError(() => of(false))
    );
  }
}
